import React, { useState } from "react";
import { toast } from "react-hot-toast";

import { useJoinVideoCall } from "features/comunications/hooks/useJoinVideoCall";
import { useVideoServiceStatus } from "features/comunications/hooks/useVideoMeetingApi";
import { ROUTES } from "utils/routes";

import styles from "./JoinVideoButton.module.css";

export const JoinVideoButton = ({
  appointment,
  className,
  onJoinVideo,
  inline = false,
}) => {
  const [isJoining, setIsJoining] = useState(false);
  const joinVideoCall = useJoinVideoCall();

  // Check video service status
  const { data: serviceStatus } = useVideoServiceStatus({
    enabled: appointment?.consultationType === "VIDEO",
  });

  const handleJoinVideo = async () => {
    if (!appointment?.id) {
      toast.error("No appointment information available");
      return;
    }

    // Check if video service is available
    if (serviceStatus && !serviceStatus.chimeAvailable) {
      toast.error(
        serviceStatus.message || "Video service is temporarily unavailable. Please try again later."
      );
      return;
    }

    setIsJoining(true);

    try {
      const videoCredentials = await joinVideoCall.mutateAsync({
        appointmentId: appointment.id,
        userType: "PROVIDER",
      });

      const meetingId =
        videoCredentials?.meetingId || videoCredentials?.Meeting?.MeetingId;
      const attendeeId =
        videoCredentials?.attendeeId || videoCredentials?.Attendee?.AttendeeId;
      const joinToken =
        videoCredentials?.joinToken || videoCredentials?.Attendee?.JoinToken;

      const audioHostUrl =
        videoCredentials?.audioHostUrl ||
        videoCredentials?.MediaPlacement?.AudioHostUrl ||
        videoCredentials?.Meeting?.MediaPlacement?.AudioHostUrl;
      const signalingUrl =
        videoCredentials?.signalingUrl ||
        videoCredentials?.MediaPlacement?.SignalingUrl ||
        videoCredentials?.Meeting?.MediaPlacement?.SignalingUrl;

      if (!meetingId || !attendeeId || !joinToken) {
        console.error("Missing credentials:", {
          hasMeetingId: !!meetingId,
          hasAttendeeId: !!attendeeId,
          hasJoinToken: !!joinToken,
        });
        throw new Error(
          "Invalid video credentials received: Missing required fields"
        );
      }

      if (!audioHostUrl || !signalingUrl) {
        console.error("Missing media configuration:", {
          hasAudioHostUrl: !!audioHostUrl,
          hasSignalingUrl: !!signalingUrl,
        });
        throw new Error(
          "Invalid video credentials received: Missing media configuration"
        );
      }

      // report-error checks reports against this appointment's current meeting.
      const callCredentials = { ...videoCredentials, appointmentId: appointment.id };

      if (inline && onJoinVideo) {
        onJoinVideo(callCredentials);
        setIsJoining(false);
        toast.success("Video call loaded");
      } else {

        await initializeAndJoinChimeMeeting(callCredentials);
        toast.success("Joining video call...");
      }
    } catch (error) {
      console.error("Failed to join video call:", error);
      toast.error(error.message || "Failed to join video call");
      setIsJoining(false);
    }
  };

  const initializeAndJoinChimeMeeting = async (credentials) => {
    const meetingId = credentials?.meetingId || credentials?.Meeting?.MeetingId;
    const joinUrl = credentials?.joinUrl;

    if (!meetingId) {
      throw new Error("Missing meeting ID in credentials");
    }

    try {

      localStorage.setItem("videoCallCredentials", JSON.stringify(credentials));

      const videoCallUrl = joinUrl || `${ROUTES.AUTH.VIDEO_CALL}?meetingId=${meetingId}`;

      const videoWindow = window.open(
        videoCallUrl,
        "_blank",
        "width=1280,height=720,menubar=no,toolbar=no,location=no,status=yes,resizable=yes"
      );

      if (!videoWindow) {
        toast.error("Please allow popups for this site to join the video call");
        localStorage.removeItem("videoCallCredentials");
        setIsJoining(false);
        return;
      }
      setIsJoining(false);
    } catch (error) {
      console.error("Error initializing Chime meeting:", error);
      toast.error("Failed to initialize video call");
      localStorage.removeItem("videoCallCredentials");
      setIsJoining(false);
      throw error;
    }
  };

  if (appointment?.consultationType !== "VIDEO") {
    return null;
  }

  return (
    <button
      className={`${styles.joinVideoBtn} ${className || ""}`}
      onClick={handleJoinVideo}
      disabled={isJoining}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M23 7l-7 5 7 5V7z" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </svg>
      <span>{isJoining ? "Joining..." : "Join Video Call"}</span>
    </button>
  );
};
